// src/features/task/utils/taskSort.ts
/**
 * Task 排序工具函數
 *
 * Smart sort 加權分數計算，供 Tasks 頁與 Today 頁共用同一排序邏輯：
 * - planScore（30%）：今天=1.0，往未來/過期均以 1/(1+|daysDiff|) 遞減，無 plan=0
 * - priorityScore（20%）：高(1)=1.0，中(2)=0.66，低(3)=0.33，無=0
 * - urgencyScore（20%）：逾期1~7天維持1.0高原，逾期7天後緩慢衰減；截止日愈近愈高，無=0
 * - freshnessScore（30%）：指數衰減 halfLife=3天；updatedAt fallback createdAt；兩者皆無=0
 */

import { Task, TaskPriority } from '../../../types/task';

export const PRIORITY_SCORE: Record<TaskPriority, number> = {
  '1': 1.0,
  '2': 0.66,
  '3': 0.33,
};

const WEIGHTS = { plan: 0.3, priority: 0.2, urgency: 0.2, freshness: 0.3 };

export function calculateSmartScore(task: Task, today: Date, now: Date = new Date()): number {
  // planScore：對稱曲線，過去未來同速遞減
  let planScore = 0;
  if (task.plan) {
    const planDate = new Date(task.plan);
    planDate.setHours(0, 0, 0, 0);
    const daysDiff = Math.round(
      (planDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    planScore = daysDiff === 0 ? 1.0 : 1 / (1 + Math.abs(daysDiff));
  }

  // priorityScore
  const priorityScore = task.priority ? PRIORITY_SCORE[task.priority] : 0;

  // urgencyScore：非對稱曲線，逾期後有一週高原期再緩降
  let urgencyScore = 0;
  if (task.targetDate) {
    const targetDate = new Date(task.targetDate);
    targetDate.setHours(0, 0, 0, 0);
    const daysUntilDue = Math.round(
      (targetDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (daysUntilDue < 0) {
      const daysOverdue = Math.abs(daysUntilDue);
      urgencyScore =
        daysOverdue <= 7 ? 1.0 : 1 / (1 + (daysOverdue - 7) * 0.15);
    } else if (daysUntilDue <= 1) {
      urgencyScore = 1.0;
    } else if (daysUntilDue <= 3) {
      urgencyScore = 0.8;
    } else if (daysUntilDue <= 7) {
      urgencyScore = 0.5;
    } else {
      urgencyScore = 1 / (1 + daysUntilDue * 0.1);
    }
  }

  let freshnessScore = 0;
  const lastUpdated = task.updatedAt ?? task.createdAt ?? null;
  if (lastUpdated) {
    const daysSince = (now.getTime() - lastUpdated.getTime()) / (1000 * 60 * 60 * 24);
    freshnessScore = Math.pow(0.5, daysSince / 3);
  }

  return planScore * WEIGHTS.plan + priorityScore * WEIGHTS.priority + urgencyScore * WEIGHTS.urgency + freshnessScore * WEIGHTS.freshness;
}

/**
 * 取 smart sort 排序後的前 N 筆 open tasks
 * 供 Today 頁等非 Tasks 頁面使用，確保排序邏輯一致
 */
export function getTopTasksBySmartSort(
  tasks: Task[],
  today: Date,
  limit: number,
  now: Date = new Date(),
): Task[] {
  const scores = new Map(tasks.map((t) => [t.id, calculateSmartScore(t, today, now)]));
  return [...tasks]
    .sort((a, b) => {
      const diff = (scores.get(b.id) ?? 0) - (scores.get(a.id) ?? 0);
      if (diff !== 0) return diff;
      return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
    })
    .slice(0, limit);
}
